import { useState, useMemo } from "react";
import { FaSearch } from "react-icons/fa";
import NoteComponent from "./table/Notes";
import DynamicLoader from "../Form/FormFields/DynamicLoader";

const formatNumberWithCommas = (value) => {
  const locale = "en-IN";
  if (value === null || value === undefined || value === "") return "0";

  // convert numeric strings
  if (typeof value === "string") {
    const cleaned = value.replace(/,/g, "");
    if (!isNaN(cleaned)) {
      return Number(cleaned).toLocaleString(locale);
    }
    return value;
  }

  // convert numbers
  if (typeof value === "number") {
    return value.toLocaleString(locale);
  }

  return value;
};

// 🟢 Optimized check for zero-only columns
const isZeroColumn = (rows, field) => {
  if (!rows || rows.length === 0) return true;

  // Check if at least one row has a meaningful (non-zero) value
  return !rows.some((row) => {
    const val = row[field];

    if (
      val === null ||
      val === undefined ||
      val === "" ||
      val === 0 ||
      val === "0"
    ) {
      return false;
    }

    // If it's a numeric-looking string, suppress only if it's zero.
    // Non-numeric text (IDs, dates, names, statuses) is always meaningful.
    if (typeof val === "string") {
      const cleaned = val.replace(/,/g, "");
      if (cleaned !== "" && !isNaN(cleaned)) {
        return Number(cleaned) !== 0;
      }
      return true;
    }

    return true; // It's a non-zero number or other truthy value
  });
};

function CustomTable({
  columns,
  rows,
  title,
  notes,
  noteData,
  isLoading,
  hideTitle = false,
}: any) {
  const [globalFilter, setGlobalFilter] = useState("");

  // 🟢 STEP 1: Remove zero-only columns
  const filteredColumns = useMemo(() => {
    if (!rows || rows.length === 0) return columns;
    return columns.filter((col) => !isZeroColumn(rows, col.field));
  }, [columns, rows]);

  // 🟢 STEP 2: Search filtering
  const filteredRows = useMemo(() => {
    if (!globalFilter) return rows;

    const searchTerm = globalFilter.toLowerCase();

    return rows.filter((row) =>
      filteredColumns.some((col) => {
        const value = row[col.field];
        return value?.toString().toLowerCase().includes(searchTerm);
      })
    );
  }, [globalFilter, rows, filteredColumns]);

  return (
    <div className="w-full h-full flex flex-col items-center">
      {/* Search & Header Section */}
      <div className="w-full mb-4">
        {!hideTitle && (
          <div className="p-5 rounded-t-2xl bg-white border-b border-gray-100 shadow-sm mb-4">
            <div className="flex flex-col md:flex-row justify-between items-center gap-4">
              <div className="flex items-center">
                <h2 className="text-xl font-bold text-slate-800 tracking-tight">
                  {title}
                </h2>
                {notes && <NoteComponent notes={noteData} modalTitle={title} />}
              </div>

              <div className="relative w-full md:w-72 group">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <FaSearch className="text-slate-400 group-focus-within:text-blue-500 transition-colors" />
                </div>
                <input
                  type="text"
                  placeholder="Search details..."
                  value={globalFilter}
                  onChange={(e) => setGlobalFilter(e.target.value)}
                  className="block w-full pl-10 pr-4 py-2 bg-slate-50 border border-slate-200 rounded-xl text-sm text-slate-700 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all shadow-sm"
                />
              </div>
            </div>
          </div>
        )}

        {hideTitle && (
          <div className="flex justify-end p-2 px-4 bg-white mb-2">
            <div className="relative w-full md:w-72 group">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <FaSearch className="text-slate-400 group-focus-within:text-blue-500 transition-colors" />
              </div>
              <input
                type="text"
                placeholder="Search details..."
                value={globalFilter}
                onChange={(e) => setGlobalFilter(e.target.value)}
                className="block w-full pl-10 pr-4 py-2 bg-slate-50 border border-slate-200 rounded-xl text-sm text-slate-700 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all shadow-sm"
              />
            </div>
          </div>
        )}
      </div>

      {/* Table Container - Shrinks based on content and centers */}
      <div className="overflow-x-auto w-full flex justify-center pb-6">
        <div className="w-fit min-w-[300px] max-w-full overflow-hidden  rounded-xl transition-all">
          <div className="max-h-[calc(100vh-280px)] overflow-y-auto scrollbar-thin-black">
            <table className="w-full text-left border-separate border-spacing-0">
              <thead className="sticky top-0 z-20">
                <tr>
                  {filteredColumns?.map((col: any, index: number) => (
                    <th
                      key={index}
                      className="sticky top-0 z-10 px-6 py-4 bg-[#1e1e2d] text-slate-200 text-[10px] font-bold uppercase tracking-widest border-b border-slate-700 text-center whitespace-nowrap"
                    >
                      {col.header}
                    </th>
                  ))}
                </tr>
              </thead>

              <tbody className="divide-y divide-slate-50">
                {isLoading ? (
                  <tr>
                    <td
                      colSpan={filteredColumns?.length || 1}
                      className="p-16 text-center"
                    >
                      <DynamicLoader loading={isLoading} />
                    </td>
                  </tr>
                ) : filteredRows?.length > 0 ? (
                  filteredRows.map((row: any, rowIndex: number) => (
                    <tr
                      key={rowIndex}
                      className="group transition-colors hover:bg-slate-50/50"
                    >
                      {filteredColumns?.map((col: any, colIndex: number) => (
                        <td
                          key={colIndex}
                          className="px-6 py-3.5 text-slate-600 text-[13px] font-medium text-center border-b border-slate-50 group-last:border-0"
                        >
                          {formatNumberWithCommas(row[col.field])}
                        </td>
                      ))}
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td
                      colSpan={filteredColumns?.length || 1}
                      className="p-16 text-center text-slate-400 font-medium italic text-sm"
                    >
                      No matching records found
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}

export default CustomTable;
